
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Trophy, Flame, Target } from 'lucide-react';
import confetti from 'canvas-confetti';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import { CustomsGate } from '../../nobel';
import { gamificationService } from '../../../services/gamificationService';
import { geminiEngine } from '../../../services/geminiEngine';

interface Word {
  word: string;
  definition: string;
  example: string;
  rarity: 'common' | 'rare' | 'legendary';
}

export const VocabularySlots: React.FC = () => {
  const userId = sessionStorage.getItem('ielts_user_id') || '';
  const { canAccess, tier } = useFeatureAccess('advanced_module', userId);
  const [paywallOpen, setPaywallOpen] = useState(false);

  const [spinning, setSpinning] = useState(false);
  const [currentWord, setCurrentWord] = useState<Word | null>(null);
  const [combo, setCombo] = useState(0);
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canAccess) {
      setPaywallOpen(true);
    }
  }, [canAccess]);

  const spin = async () => {
    if (spinning || loading) return;
    setSpinning(true);
    setLoading(true);

    try {
      // Use Gemini to get a random interesting word if possible, or simulate logic
      // For speed, we simulate the "spin" visual while fetching
      const minSpinTime = new Promise(resolve => setTimeout(resolve, 2000));

      const fetchWord = async () => {
        try {
          // We can use Vocabulary generation from Gemini but limit to 1
          const vocabSet = await geminiEngine.generateVocabularySet("Academic, Science, or Arts");
          const item = vocabSet.words[0];
          const rarityVal = Math.random();
          const rarity = rarityVal > 0.9 ? 'legendary' : rarityVal > 0.6 ? 'rare' : 'common';
          return {
            word: item.word,
            definition: item.def,
            example: item.ex,
            rarity: rarity as any
          };
        } catch (e) {
          // Fallback
          return {
            word: 'UBIQUITOUS',
            definition: 'Present everywhere simultaneously',
            example: 'Smartphones have become ubiquitous in modern society.',
            rarity: 'rare' as const
          };
        }
      };

      const [_, word] = await Promise.all([minSpinTime, fetchWord()]);

      setCurrentWord(word);
      setSpinning(false);
      setLoading(false);
      setStreak(prev => prev + 1);

      // Jackpot animation
      if (word.rarity === 'legendary') {
        confetti({
          particleCount: 200,
          spread: 100,
          origin: { y: 0.6 },
          colors: ['#C5A059', '#f59e0b', '#ef4444']
        });
        setCombo(prev => prev + 2);
        if (userId) gamificationService.awardXP(userId, 'achievement_unlock'); // Bonus XP
      } else {
        setCombo(prev => prev + 1);
      }

      if (userId) {
        gamificationService.awardXP(userId, 'writing_submit'); // Re-using an XP trigger for simplicity
      }

    } catch (e) {
      setSpinning(false);
      setLoading(false);
    }
  };

  return (
    <>
      <div className="h-full bg-gradient-to-br from-[#1c1917] via-[#0a0a0a] to-[#1c1917] rounded-3xl p-8 relative overflow-hidden flex flex-col">
        {/* Animated Background Pattern */}
        <motion.div
          className="absolute inset-0 opacity-5 pointer-events-none"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: 'reverse'
          }}
          style={{
            backgroundImage: 'repeating-linear-gradient(45deg, #C5A059 0px, transparent 10px, transparent 20px)',
            backgroundSize: '200% 200%'
          }}
        />

        {/* Header */}
        <div className="relative z-10 flex justify-between items-center mb-12">
          <div>
            <h2 className="text-4xl font-black text-white mb-2">VOCABULARY SLOTS</h2>
            <p className="text-stone-400 mono text-sm">Spin to Master • Build Combos • Win Big</p>
          </div>

          <div className="flex gap-4">
            <motion.div
              className="bg-white/5 border border-white/10 rounded-xl px-6 py-3"
              animate={{ scale: combo > 0 ? [1, 1.05, 1] : 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Flame className="text-orange-500" size={16} />
                <span className="text-xs mono text-stone-500 uppercase">Combo</span>
              </div>
              <div className="text-3xl font-black text-[#C5A059]">{combo}x</div>
            </motion.div>

            <motion.div
              className="bg-white/5 border border-white/10 rounded-xl px-6 py-3"
              animate={{ scale: streak > 0 ? [1, 1.05, 1] : 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Target className="text-blue-500" size={16} />
                <span className="text-xs mono text-stone-500 uppercase">Streak</span>
              </div>
              <div className="text-3xl font-black text-[#C5A059]">{streak}</div>
            </motion.div>
          </div>
        </div>

        {/* Slot Machine */}
        <div className="relative z-10 max-w-4xl mx-auto w-full flex-1 flex flex-col justify-center">
          <div className="bg-gradient-to-b from-[#C5A059] to-[#B48F48] p-8 rounded-3xl shadow-2xl border-4 border-[#1c1917]">
            {/* Reels Container */}
            <div className="bg-[#0a0a0a] rounded-2xl p-12 mb-8 min-h-[400px] flex items-center justify-center relative overflow-hidden">
              {spinning ? (
                // Spinning Animation
                <div className="flex gap-8">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className="w-32 h-32 bg-white/10 rounded-xl"
                      animate={{
                        y: [0, -1000],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "linear",
                        delay: i * 0.1
                      }}
                    />
                  ))}
                </div>
              ) : currentWord ? (
                // Word Display
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 260,
                    damping: 20
                  }}
                  className="text-center w-full"
                >
                  {/* Rarity Badge */}
                  <motion.div
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${currentWord.rarity === 'legendary'
                        ? 'bg-purple-500/20 border-2 border-purple-500'
                        : currentWord.rarity === 'rare'
                          ? 'bg-[#C5A059]/20 border-2 border-[#C5A059]'
                          : 'bg-white/10 border-2 border-white/20'
                      }`}
                    animate={currentWord.rarity === 'legendary' ? {
                      boxShadow: [
                        '0 0 0px rgba(168, 85, 247, 0)',
                        '0 0 20px rgba(168, 85, 247, 0.5)',
                        '0 0 0px rgba(168, 85, 247, 0)'
                      ]
                    } : {}}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {currentWord.rarity === 'legendary' && <Trophy className="text-purple-400" size={20} />}
                    {currentWord.rarity === 'rare' && <Sparkles className="text-[#C5A059]" size={20} />}
                    <span className="text-xs mono font-black uppercase text-white">
                      {currentWord.rarity}
                    </span>
                  </motion.div>

                  <h3 className="text-5xl md:text-6xl font-black text-white mb-6 tracking-tight break-words">
                    {currentWord.word}
                  </h3>

                  <p className="text-xl text-[#C5A059] mb-8 italic">
                    {currentWord.definition}
                  </p>

                  <div className="bg-white/5 rounded-xl p-6 border border-white/10 max-w-2xl mx-auto">
                    <p className="text-stone-300 leading-relaxed text-lg">
                      "{currentWord.example}"
                    </p>
                  </div>
                </motion.div>
              ) : (
                // Empty State
                <div className="text-center text-stone-500">
                  <Sparkles size={64} className="mx-auto mb-4 opacity-20" />
                  <p className="mono text-sm">Press SPIN to begin</p>
                </div>
              )}
            </div>

            {/* Spin Button */}
            <motion.button
              onClick={spin}
              disabled={spinning}
              className="w-full py-8 bg-gradient-to-r from-[#ef4444] to-[#dc2626] text-white font-black text-2xl rounded-xl shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 active:scale-95 transition-all"
              whileHover={{ scale: spinning ? 1 : 1.02 }}
              whileTap={{ scale: spinning ? 1 : 0.98 }}
            >
              {spinning ? (
                <motion.span
                  animate={{ opacity: [1, 0.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  SPINNING...
                </motion.span>
              ) : (
                'SPIN TO LEARN'
              )}
            </motion.button>
          </div>

          {/* Bottom Stats */}
          <div className="grid grid-cols-3 gap-4 mt-8">
            {[
              { label: 'Words Mastered', value: streak * 12 + combo, color: 'blue' },
              { label: 'Global Rank', value: '#1,284', color: 'green' },
              { label: 'XP Multiplier', value: `${(1 + combo * 0.1).toFixed(1)}x`, color: 'purple' }
            ].map((stat) => (
              <div key={stat.label} className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
                <div className="text-sm mono text-stone-500 uppercase mb-2">{stat.label}</div>
                <div className="text-2xl font-black text-white">{stat.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        feature="Vocabulary Slots"
        reason="premium_feature"
        currentTier={tier}
      />
    </>
  );
};
